import { useCallback, useMemo } from 'react'

import { differenceInSeconds } from 'date-fns'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedAuctionListingDetails,
  type OrchestratedOfferingListingDetails,
  type OrchestratedQuote,
  isAuctionListing,
  isOfferingListing,
} from '@b-stock/fe-core/bstock-next/queries'
import { parseOptionalISODate } from '@b-stock/fe-core/bstock-react'

import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import { useListingP2SData } from '@/buyer-portal/hooks/useListingP2SData'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { useProblemFreeOrderRateAnalytics } from '../hooks/useProblemFreeOrderRateAnalytics'

// Null rather than NaN when the window has no end time: an unparseable date
// would otherwise reach analytics as `time_left: NaN`.
const timeLeftInSeconds = (endTime?: string) => {
  const end = parseOptionalISODate(endTime)
  return end ? differenceInSeconds(end, new Date()) : null
}

export const useTrackActionColumnClick = ({
  data,
  offerState,
  selectedQuote,
  winningState,
}: {
  data: OrchestratedAuctionListingDetails | OrchestratedOfferingListingDetails
  /**
   * Offer-lifecycle standing, the offer analog of `winningState` — see
   * `useOfferDecisionState`. Only supplied for offering listings.
   */
  offerState?: string
  selectedQuote?: OrchestratedQuote | null
  winningState?: string
}) => {
  const { trackButtonClicked } = useAnalytics()
  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)
  const p2sData = useListingP2SData(data.lot.locationOriginalId)
  const problemFreeOrderRateAnalytics = useProblemFreeOrderRateAnalytics(data)

  const specificAnalyticsData = useMemo(() => {
    if (isAuctionListing(data)) {
      return {
        ...getListingAnalyticsData(data, fmvBadgeFlag),
        time_left: timeLeftInSeconds(data.auction.actualEndTime),
        bids_existing: data.auction.numberOfBids,
        ...(selectedQuote && {
          winning_state: winningState,
        }),
      }
    }

    if (isOfferingListing(data)) {
      return {
        ...getListingAnalyticsData(data, fmvBadgeFlag, p2sData),
        ...problemFreeOrderRateAnalytics,
        offer_state: offerState,
        time_left: timeLeftInSeconds(data.offering?.offerEndTime),
      }
    }

    return undefined
  }, [
    data,
    fmvBadgeFlag,
    offerState,
    p2sData,
    problemFreeOrderRateAnalytics,
    selectedQuote,
    winningState,
  ])

  const trackButtonClick = useCallback(
    (buttonName: string) => {
      trackButtonClicked({
        buttonName,
        screenName: 'listing_detail_page',
        ...specificAnalyticsData,
      })
    },
    [trackButtonClicked, specificAnalyticsData]
  )

  return { trackButtonClick }
}
